import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLenis } from "lenis/react";
import { ArrowDown } from "lucide-react";

const LINES = ["EVERY GREAT", "JOURNEY BEGINS", "WITH A VISION."];

const lineVariants = {
  hidden: { y: "115%" },
  visible: (i) => ({
    y: "0%",
    transition: {
      delay: 0.55 + i * 0.16,
      type: "spring",
      stiffness: 60,
      damping: 16,
      mass: 1.1,
    },
  }),
};

export const Hero = () => {
  const ref = useRef(null);
  const lenis = useLenis();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section
      ref={ref}
      id="top"
      className="relative min-h-screen flex items-center overflow-hidden"
      data-testid="hero-section"
    >
      <motion.div style={{ y: bgY }} className="absolute inset-0">
        <img
          src="https://images.unsplash.com/photo-1639674242803-a9de33b3a835?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwyfHxibGFjayUyMGdvbGQlMjBtYXJibGUlMjB0ZXh0dXJlfGVufDB8fHx8MTc4NTgwODc0M3ww&ixlib=rb-4.1.0&q=85"
          alt=""
          className="h-[120%] w-full object-cover object-right opacity-80 [filter:brightness(1.15)]"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/85 to-ink/20" />
        <div className="absolute inset-0 bg-gradient-to-b from-ink/70 via-transparent to-ink" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative w-full max-w-[1600px] mx-auto px-6 md:px-12 pt-24">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.2, delay: 0.3 }}
          className="text-xs uppercase tracking-[0.35em] text-gold mb-10"
          data-testid="hero-overline"
        >
          RayStance Eyewear
        </motion.p>

        <h1 className="font-serif font-light leading-[0.95] tracking-tight text-[13vw] md:text-[9vw] lg:text-[7.5vw]">
          {LINES.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                custom={i}
                variants={lineVariants}
                initial="hidden"
                animate="visible"
                className={`block ${i === 2 ? "italic gold-text-gradient" : "text-white"}`}
                data-testid={`hero-line-${i + 1}`}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.5, ease: [0.22, 1, 0.36, 1] }}
          className="mt-14 flex flex-col sm:flex-row sm:items-center gap-8"
        >
          <button
            onClick={() => lenis?.scrollTo("#collection", { offset: -40, duration: 1.8 })}
            className="group relative inline-flex items-center gap-3 rounded-full border border-gold/60 px-10 py-4 text-xs uppercase tracking-[0.25em] text-gold overflow-hidden transition-colors duration-500 hover:text-ink"
            data-testid="hero-explore-button"
          >
            <span className="absolute inset-0 bg-gold translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-out" />
            <span className="relative">Explore the Collection</span>
          </button>
          <p className="text-sm text-ash max-w-xs leading-relaxed">
            Luxury frames for those who stand firm in who they are.
          </p>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.2, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-gold/60"
      >
        <ArrowDown className="h-4 w-4 animate-bounce" />
      </motion.div>
    </section>
  );
};
